import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  ArrowLeft, Search, ChevronDown, ChevronUp, Sparkles, Car,
  GraduationCap, Users, Camera, X, Send,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import BrowserNarration from "../components/BrowserNarration";

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = { car: Car, graduation: GraduationCap, users: Users, camera: Camera };

/**
 * HelpCentre — role-based user manuals + shared Camera/Upload guide.
 *
 * Routes:
 *   /help                 → landing (natural-language input + tiles)
 *   /help?q=<subject>     → renders the role-aware Knowledge Package
 *                           answer for <subject> inline (canonical
 *                           C4b Universal Help path).
 *   /help/:manualId       → single manual with search + step cards
 *   /help/:manualId?s=…   → deep-link to a section (expanded + scrolled to)
 *
 * Mobile-first, searchable, Karen narration per section, context-linkable.
 */
export default function HelpCentre() {
  const { manualId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  if (!manualId) return <ManualsLanding user={user} nav={nav} />;
  return <SingleManual manualId={manualId} nav={nav} />;
}// ─── Landing: natural-language input + optional tile browse ───
function ManualsLanding({ user, nav }) {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [draft, setDraft] = useState(q);
  const [answer, setAnswer] = useState(null);
  const [answerLoading, setAnswerLoading] = useState(false);
  // Slice C · session reuse (2026-08-17). One Universal Intent
  // session per Help-page lifecycle for the current authenticated
  // identity. Cleared/recreated when `user?.id` changes.
  const uiSessionRef = useRef({ userId: null, sid: null });

  useEffect(() => {
    axios.get(`${API}/api/help/manuals`).then(r => setTiles(r.data.items || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  // C4b · when the URL carries ?q=<subject>, fetch the role-aware
  // Knowledge Package answer. Role is read from the session on the
  // server — never sent from the client — so the answer cannot be
  // impersonated by URL manipulation.
  useEffect(() => {
    if (!q.trim()) { setAnswer(null); return; }
    setAnswerLoading(true);
    axios.get(`${API}/api/help/answer`, {
      params: { subject: q },
      withCredentials: true,
    })
      .then(r => setAnswer(r.data))
      .catch(e => setAnswer({
        known: false,
        subject: q,
        hint: e?.response?.data?.detail || "I couldn't retrieve that just now.",
      }))
      .finally(() => setAnswerLoading(false));
  }, [q]);

  const submit = async (utterance) => {
    const text = (utterance || "").trim();
    if (!text) return;
    // Slice C · migrated to Universal Intent (2026-08-17).
    // Same authenticated session convention used by
    // UniversalContextBar. The ui_help_lookup specialist returns
    // an `answered` envelope with `help.target_path` we may apply
    // in-place, or a `clarify` / `unavailable` outcome.
    try {
      const currentUserId = (user && (user.id || user._id)) || null;
      // Slice C: reuse the session for the page lifecycle; drop
      // and recreate if the authenticated identity changes.
      if (uiSessionRef.current.userId !== currentUserId) {
        uiSessionRef.current = { userId: currentUserId, sid: null };
      }
      let sid = uiSessionRef.current.sid;
      if (!sid) {
        const s = await axios.post(
          `${API}/api/universal_intent/session`,
          {},
          { withCredentials: true },
        );
        sid = s.data && s.data.session_id;
        if (!sid) throw new Error("session_missing");
        uiSessionRef.current.sid = sid;
      }
      const r = await axios.post(
        `${API}/api/universal_intent/session/${sid}/turn`,
        {
          text,
          input_mode: "text",
          operation_id: `help-${Date.now()}`,
          page_context: { route: "/help", surface: "help_centre" },
        },
        { withCredentials: true },
      );
      const dr = (r.data && r.data.dispatch_result) || {};
      const help = dr.help || null;
      const target = help && help.target_path;
      if (target && target.startsWith("/help/")) {
        // Apply in-place (no full navigation) when the match is a
        // manual on this same Help surface. Update the query so
        // the answer renders inline.
        nav(target);
      } else if (target && target.startsWith("/help")) {
        const url = new URL(target, window.location.origin);
        const nextQ = url.searchParams.get("q") || text;
        setSearchParams({ q: nextQ });
        setDraft(nextQ);
      } else if (dr.outcome === "clarify" || dr.outcome === "unavailable") {
        // Show the clarify/unavailable narration inline by
        // updating the query so the existing hint UX renders.
        setSearchParams({ q: text });
        setDraft(text);
      } else {
        setSearchParams({ q: text });
        setDraft(text);
      }
    } catch (_e) {
      setSearchParams({ q: text });
      setDraft(text);
    }
  };

  const roleSuggestion = useMemo(() => {
    if (!user) return null;
    const map = { learner: "learner", pdi: "pdi", adi: "adi" };
    return map[user.role] || null;
  }, [user]);

  const homePath = useMemo(() => {
    if (!user) return "/login";
    const role = (user.admin_view_role || user.role || "").toLowerCase();
    if (role === "admin") return "/today";
    if (role === "adi" || role === "pdi") return "/instructor";
    return "/dashboard";
  }, [user]);

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-20">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="help-back"
            onClick={() => nav(homePath)}
            title="Back to home"
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            <p className="text-[10px] font-extrabold tracking-wider text-blue-600 uppercase">Help Centre</p>
            <p className="text-sm font-bold text-slate-900 dark:text-white">How to use your system</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* C4b · Universal natural-language help input. Same
            pipeline as ConversationBar — voice + text share this
            path. Not required to browse; the tiles below remain. */}
        <form
          data-testid="help-nl-form"
          onSubmit={(e) => { e.preventDefault(); submit(draft); }}
          className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full shadow-sm px-2 py-1.5"
        >
          <Sparkles className="w-4 h-4 ml-2 text-blue-500 flex-shrink-0" />
          <Input
            data-testid="help-nl-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder='Ask anything — e.g. "help roundabouts"'
            className="flex-1 bg-transparent border-0 focus-visible:ring-0 shadow-none h-10"
          />
          <button
            type="submit"
            data-testid="help-nl-submit"
            disabled={!draft.trim()}
            aria-label="Ask"
            className="shrink-0 p-2 rounded-full bg-slate-900 text-white disabled:bg-slate-300"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>

        {(answerLoading || answer) && (
          <div
            data-testid="help-answer"
            data-known={answer ? String(answer.known) : "loading"}
            className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-2"
          >
            {answerLoading && (
              <p className="text-sm text-slate-500">Looking that up…</p>
            )}
            {!answerLoading && answer && answer.known && (
              <>
                <p
                  data-testid="help-answer-title"
                  className="text-base font-extrabold text-slate-900 dark:text-white"
                >
                  {answer.title}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                  For {answer.role}
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-line">
                  {answer.body}
                </p>
                {Array.isArray(answer.prompts) && answer.prompts.length > 0 && (
                  <div className="pt-2 space-y-1">
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400">Try next:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {answer.prompts.map((p, i) => (
                        <button
                          key={i}
                          data-testid={`help-answer-prompt-${i}`}
                          onClick={() => submit(p)}
                          className="px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600"
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {!answerLoading && answer && !answer.known && (
              <p
                data-testid="help-answer-hint"
                className="text-sm text-slate-600 dark:text-slate-300"
              >
                {answer.hint || "I don't have specific guidance for that yet."}
              </p>
            )}
          </div>
        )}

        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white mb-1">
            Pick a guide
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Short cards, plain English. Everything you need in under a minute.
          </p>
        </div>

        {loading && <p className="text-sm text-slate-400">Loading…</p>}

        <div data-testid="help-tiles" className="space-y-3">
          {tiles.map(t => {
            const Icon = ICONS[t.icon] || Sparkles;
            const suggested = roleSuggestion === t.id;
            return (
              <button
                key={t.id}
                data-testid={`help-tile-${t.id}`}
                onClick={() => nav(`/help/${t.id}`)}
                className={`w-full text-left rounded-2xl border p-4 flex items-start gap-4 active:scale-[0.99] transition-all
                  ${suggested
                    ? "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/15 dark:to-indigo-900/10 border-blue-200 dark:border-blue-800"
                    : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0
                  ${suggested ? "bg-blue-600" : "bg-slate-100 dark:bg-slate-700"}`}>
                  <Icon className={`w-5 h-5 ${suggested ? "text-white" : "text-slate-600 dark:text-slate-300"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-extrabold text-slate-900 dark:text-white">{t.title}</p>
                    {suggested && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-600 text-white">FOR YOU</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{t.subtitle}</p>
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 text-center mt-6 leading-relaxed">
          Tap "Something confused me" on any screen to tell us what's missing here.
        </p>
      </div>
    </div>
  );
}

// ─── Single manual viewer with search + expandable sections ───
function SingleManual({ manualId, nav }) {
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = show manual; [] or [items] = show search results
  const deepLinkSection = searchParams.get("s");
  const [expanded, setExpanded] = useState(deepLinkSection ? new Set([deepLinkSection]) : new Set());

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/help/manuals/${manualId}`)
      .then(r => setManual(r.data))
      .catch(() => setManual(null))
      .finally(() => setLoading(false));
  }, [manualId]);

  // Debounced search
  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/help/search`, { params: { q, manual_id: manualId } })
        .then(r => setResults(r.data.results || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, manualId]);

  const toggleSection = (sid) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const openSection = useCallback((sid) => {
    setQuery("");
    setResults(null);
    setExpanded(prev => new Set([...prev, sid]));
    setSearchParams({ s: sid });
    setTimeout(() => {
      document.getElementById(`help-section-${sid}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [setSearchParams]);

  useEffect(() => {
    if (deepLinkSection && manual) openSection(deepLinkSection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual]);

  if (loading) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>;
  }

  if (!manual) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120] px-6 text-center">
      <div>
        <p className="text-slate-500 mb-4">We couldn't find that guide</p>
        <Button onClick={() => nav("/help")} data-testid="help-back-to-list">Back to Help</Button>
      </div>
    </div>;
  }

  const Icon = ICONS[manual.icon] || Sparkles;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-20">
      {/* Sticky header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="help-back" onClick={() => nav("/help")}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold tracking-wider text-blue-600 uppercase">Help</p>
            <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{manual.title}</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Title + subtitle */}
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h1 data-testid="help-manual-title" className="text-xl font-extrabold text-slate-900 dark:text-white leading-tight">{manual.title}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{manual.subtitle}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            data-testid="help-search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search this guide…"
            className="pl-9 pr-9 h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
          />
          {query && (
            <button
              data-testid="help-search-clear"
              onClick={() => { setQuery(""); setResults(null); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>

        {/* Search results OR sections */}
        {results !== null ? (
          <SearchResults
            results={results}
            query={query}
            onOpen={(sid) => openSection(sid)}
          />
        ) : (
          <div data-testid="help-sections" className="space-y-3">
            {manual.sections.map((s, idx) => (
              <SectionCard
                key={s.id}
                section={s}
                index={idx + 1}
                expanded={expanded.has(s.id)}
                onToggle={() => toggleSection(s.id)}
              />
            ))}
          </div>
        )}

        {/* Always-visible exit at the bottom of the page so users never
            get stuck after scrolling deep into the manual. */}
        <div className="pt-4 flex justify-center">
          <Button
            data-testid="help-manual-done"
            onClick={() => nav("/help")}
            variant="outline"
            className="h-11 px-6 rounded-xl text-sm font-bold"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Help index
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Individual expandable section card with TTS ───
function SectionCard({ section, index, expanded, onToggle }) {
  const narrationText = [section.title + ".", section.summary,
    ...section.steps.flatMap(step => [step.title + ".", step.body]),
    section.tip ? "Tip: " + section.tip : ""].filter(Boolean).join(" ");

  return (
    <div
      id={`help-section-${section.id}`}
      data-testid={`help-section-${section.id}`}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    >
      <button
        data-testid={`help-section-toggle-${section.id}`}
        onClick={onToggle}
        className="w-full p-4 flex items-start gap-3 text-left active:bg-slate-50 dark:active:bg-slate-700/40"
      >
        <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-extrabold text-blue-600">{index}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-extrabold text-slate-900 dark:text-white leading-snug">{section.title}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{section.summary}</p>
        </div>
        {expanded
          ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-0 space-y-3 border-t border-slate-100 dark:border-slate-700">
          <div className="mt-3" data-testid={`help-section-audio-${section.id}`}>
            <BrowserNarration text={narrationText} title={section.title} />
          </div>

          {/* Steps */}
          <ol className="space-y-2.5">
            {section.steps.map((step, i) => (
              <li key={i} data-testid={`help-step-${section.id}-${i}`} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[11px] font-extrabold text-slate-600 dark:text-slate-300">{i + 1}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{step.title}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* Tip */}
          {section.tip && (
            <div className="rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-900/40 p-3 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                <span className="font-bold">Tip: </span>{section.tip}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Search results view ───
function SearchResults({ results, query, onOpen }) {
  if (results.length === 0) {
    return (
      <div data-testid="help-search-empty" className="text-center py-12 text-sm text-slate-500 dark:text-slate-400">
        <p>Nothing matches "<span className="font-bold">{query}</span>" in this guide.</p>
        <p className="mt-1 text-xs text-slate-400">Try shorter words — e.g. "upload" instead of "uploading files".</p>
      </div>
    );
  }
  return (
    <div data-testid="help-search-results" className="space-y-2">
      <p className="text-xs text-slate-500 dark:text-slate-400">{results.length} match{results.length === 1 ? "" : "es"}</p>
      {results.map((r, i) => (
        <button
          key={`${r.section_id}-${i}`}
          data-testid={`help-search-result-${i}`}
          onClick={() => onOpen(r.section_id)}
          className="w-full text-left bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 hover:border-blue-300 active:scale-[0.99]"
        >
          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">{r.section_title}</p>
          <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">{r.step_title}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{r.step_body}</p>
        </button>
      ))}
    </div>
  );
}

